import React from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const DrawnStringsList: React.FC = () => {
  const {
    pagePathsMap,
    pixelsPerFoot,
    removeDrawnString,
    editDrawnStringLength,
  } = useApp();

  const allStrings: Array<{
    page: number;
    idx: number;
    name: string;
    displayLen: string;
    overrideLen: number | null | undefined;
  }> = [];

  Object.keys(pagePathsMap).forEach((pStr) => {
    const page = parseInt(pStr, 10);
    const items = pagePathsMap[page] || [];
    items.forEach((item, idx) => {
      if (item.hidden) return;

      let displayLen: string;
      if (item.overrideLen !== null && item.overrideLen !== undefined) {
        displayLen = parseFloat(String(item.overrideLen)).toFixed(1);
      } else {
        let pxLen = 0;
        const path = item.path || [];
        for (let i = 1; i < path.length; i++) {
          if (!path[i] || !path[i - 1]) continue;
          const dx = path[i].x - path[i - 1].x;
          const dy = path[i].y - path[i - 1].y;
          pxLen += Math.sqrt(dx * dx + dy * dy);
        }
        const ftLen = pixelsPerFoot ? pxLen / pixelsPerFoot : 0;
        displayLen = ftLen.toFixed(1);
      }

      allStrings.push({
        page,
        idx,
        name: item.name || `String - ${idx + 1}`,
        displayLen,
        overrideLen: item.overrideLen,
      });
    });
  });

  if (allStrings.length === 0) {
    return null;
  }

  const handleEdit = (page: number, idx: number, currentVal?: number | null) => {
    const inputVal = prompt(
      'Enter override length in feet (leave blank to revert to drawn scale):',
      currentVal !== null && currentVal !== undefined ? String(currentVal) : ''
    );
    if (inputVal !== null) {
      if (inputVal.trim() === '') {
        editDrawnStringLength(page, idx, null);
      } else {
        const parsed = parseFloat(inputVal);
        if (!isNaN(parsed)) {
          editDrawnStringLength(page, idx, parsed);
        }
      }
    }
  };

  const handleDelete = (page: number, idx: number, name: string) => {
    if (confirm(`Are you sure you want to delete ${name}?`)) {
      removeDrawnString(page, idx);
    }
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 12px 0' }}>
        Drawn Strings
      </h2>

      <div
        style={{
          padding: '16px',
          background: 'var(--surface)',
          border: '1px solid var(--surface-border)',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {allStrings.map(({ page, idx, name, displayLen, overrideLen }) => (
          <div
            key={`${page}-${idx}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 0',
              borderBottom: '1px dashed var(--border-color)',
            }}
          >
            <span
              title={name}
              style={{
                fontSize: '0.9rem',
                fontWeight: 500,
                maxWidth: '240px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '0.95rem' }}>
                {displayLen} ft
              </span>

              <button
                type="button"
                className="tool-button"
                style={{ width: '28px', height: '28px' }}
                title="Edit Length"
                onClick={() => handleEdit(page, idx, overrideLen)}
              >
                <Edit2 className="h-3 w-3" />
              </button>

              <button
                type="button"
                className="tool-button"
                style={{ width: '28px', height: '28px' }}
                title="Delete String"
                onClick={() => handleDelete(page, idx, name)}
              >
                <Trash2 className="h-3 w-3 text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
